import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { Plus, Trash2, RefreshCw } from 'lucide-react'
import { useSquareProductMappings } from '@/hooks/useSquareProductMappings'
import { useSquareSync } from '@/hooks/useSquareSync'
import { useDataCache } from '@/contexts/DataCacheContext'

export default function SquareProductMappings() {
  const { mappings, loading, createMapping, deleteMapping } = useSquareProductMappings()
  const { fetchCatalog, catalogLoading } = useSquareSync()
  const { snapshot } = useDataCache()
  const [catalog, setCatalog] = useState([])
  const [showDialog, setShowDialog] = useState(false)
  const [selectedSquareItem, setSelectedSquareItem] = useState('')
  const [selectedProduct, setSelectedProduct] = useState('')
  const [selectedProductType, setSelectedProductType] = useState('')
  const [weightPerUnit, setWeightPerUnit] = useState('')
  const [saving, setSaving] = useState(false)

  const handleRefreshCatalog = async () => {
    const items = await fetchCatalog()
    setCatalog(items)
  }

  const handleOpenDialog = async () => {
    if (catalog.length === 0) {
      await handleRefreshCatalog()
    }
    setShowDialog(true)
    setSelectedSquareItem('')
    setSelectedProduct('')
    setSelectedProductType('')
    setWeightPerUnit('')
  }

  const handleSave = async () => {
    const squareItem = catalog.find(c => c.variation_id === selectedSquareItem)
    if (!squareItem || !selectedProduct || !selectedProductType || !weightPerUnit) return

    setSaving(true)
    try {
      await createMapping({
        square_catalog_id: squareItem.catalog_id,
        square_variation_id: squareItem.variation_id,
        square_item_name: squareItem.item_name,
        square_variation_name: squareItem.variation_name,
        odin_product_id: selectedProduct,
        odin_product_type_id: selectedProductType,
        weight_per_unit_kg: parseFloat(weightPerUnit) / 1000
      })
      setShowDialog(false)
    } finally {
      setSaving(false)
    }
  }

  // Filter out already-mapped Square items
  const mappedVariationIds = new Set(mappings.map(m => m.square_variation_id))
  const unmappedCatalog = catalog.filter(c => !mappedVariationIds.has(c.variation_id))

  const products = snapshot.products?.filter(p => p.active) || []
  const productTypes = snapshot.productTypes || []

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Product Mappings</CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleRefreshCatalog} disabled={catalogLoading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${catalogLoading ? 'animate-spin' : ''}`} />
                Refresh Catalog
              </Button>
              <Button size="sm" onClick={handleOpenDialog}>
                <Plus className="h-4 w-4 mr-2" />
                Add Mapping
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : mappings.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No product mappings configured. Add mappings to link Square catalog items to Odin products.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-2 font-medium text-muted-foreground">Square Item</th>
                    <th className="pb-2 font-medium text-muted-foreground">Odin Product</th>
                    <th className="pb-2 font-medium text-muted-foreground">Type</th>
                    <th className="pb-2 font-medium text-muted-foreground text-right">Punnet</th>
                    <th className="pb-2 w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {mappings.map(m => (
                    <tr key={m.id} className="border-b last:border-0">
                      <td className="py-2">{m.square_item_name}</td>
                      <td className="py-2">{m.odin_product?.strain || '—'}</td>
                      <td className="py-2">
                        <Badge variant="outline" className="text-xs">{m.odin_product_type?.name || '—'}</Badge>
                      </td>
                      <td className="py-2 text-right text-muted-foreground">
                        {m.weight_per_unit_kg ? `${Math.round(parseFloat(m.weight_per_unit_kg) * 1000)}g` : '—'}
                      </td>
                      <td className="py-2 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteMapping(m.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Mapping Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Product Mapping</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Square Item</Label>
              <Select value={selectedSquareItem} onValueChange={setSelectedSquareItem}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select a Square item..." />
                </SelectTrigger>
                <SelectContent>
                  {unmappedCatalog.map(item => (
                    <SelectItem key={item.variation_id} value={item.variation_id}>
                      {item.item_name} — {item.variation_name} ({item.price_formatted})
                    </SelectItem>
                  ))}
                  {unmappedCatalog.length === 0 && (
                    <SelectItem value="_none" disabled>
                      {catalog.length === 0 ? 'No catalog loaded' : 'All items mapped'}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Odin Product</Label>
              <Select value={selectedProduct} onValueChange={setSelectedProduct}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select a product..." />
                </SelectTrigger>
                <SelectContent>
                  {products.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.strain}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Product Type</Label>
              <Select value={selectedProductType} onValueChange={setSelectedProductType}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select a type..." />
                </SelectTrigger>
                <SelectContent>
                  {productTypes.map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Punnet Weight (grams)</Label>
              <Input
                type="number"
                step="1"
                min="1"
                value={weightPerUnit}
                onChange={(e) => setWeightPerUnit(e.target.value)}
                className="mt-1"
                placeholder="e.g. 150 for a 150g punnet"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Converts Square unit quantity to kg for Odin
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button
              onClick={handleSave}
              disabled={!selectedSquareItem || !selectedProduct || !selectedProductType || !weightPerUnit || saving}
            >
              {saving ? 'Saving...' : 'Save Mapping'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
